// react
import React, { useEffect, useState } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// @mui
import { useMediaQuery, Box, SwipeableDrawer, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button, IconButton, Typography, TextField, Tooltip, useTheme } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// components
import { Puller } from "components/common/Puller";
import { LoadingDialog } from "components/loading/LoadingComponent";

// features/group
import postGroup from "features/group/postGroup";

// A ---------------------------------------------------------------------- M

const CreateGroupDialog = ({ open, setOpen, handleSuccessful, handleError }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [groupname, setGroupname] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handlePopstate = () => open && handleReset();
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  const handleReset = () => {
    setGroupname("");
    setOpen(false);
  };

  const handleCreateGroup = async () => {
    setIsLoading(true);

    try {
      await postGroup(user.uid, groupname);
      handleSuccessful();
    } catch (error) {
      console.error("Error in handleCreateGroup:", error);
      handleError();
    } finally {
      setIsLoading(false);
      handleReset();
    }
  };

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item>
        <Typography variant="h6" fontWeight={700}>
          {t("new_group")}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={t("close")} placement="top">
          <IconButton onClick={handleReset} color="error" edge="end">
            <CloseOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );

  const innerContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          margin="dense"
          label={t("name")}
          variant="outlined"
          value={groupname}
          onChange={(e) => setGroupname(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#ffffff",
            },
          }}
        />
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return <LoadingDialog open={isLoading} />;
  }

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleReset}
      onOpen={() => setOpen(true)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "90%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
      data-testid="group-create-modal"
    >
      <Puller />

      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
          {t("new_group")}
        </Typography>

        {innerContent}

        <Grid container spacing={2} mt={5}>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
              onClick={handleCreateGroup}
              disabled={!groupname}
            >
              {t("confirm")}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="text"
              sx={{
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={handleReset}
            >
              {t("cancel")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={handleReset}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
      data-testid="group-create-modal"
    >
      <DialogTitle>
        <DialogTitleContent />
      </DialogTitle>

      <DialogContent>{innerContent}</DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleReset}
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleCreateGroup}
          disabled={!groupname}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateGroupDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  handleSuccessful: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
};

export default CreateGroupDialog;
